/* eslint-disable @ali/no-unused-vars */
import { get, camelCase } from 'lodash-es';
import { getAutoLogData, setAutoLogMetaInfo } from './goldlog';
import { getSpm } from './spm';

if (!window.__aplus_dynamic_data) window.__aplus_dynamic_data = {};

export const aplusDynamicData = window.__aplus_dynamic_data;

const dynamicTrackInfoConfig = {
  cssSelector: '.dynamic-track-info',
  trackInfo: 'dynamic-track-info',
};
export const ELEMENT_INDEX = Symbol.for('ELEMENT_INDEX');
let forceCheckLock = false;
const forceCheckAplusMO = () => {
  if (forceCheckLock) return;
  const el = document.createElement('div');
  el.style.display = 'none';
  forceCheckLock = true;
  setTimeout(() => {
    document.body.appendChild(el);
    setTimeout(() => {
      el.remove();
      forceCheckLock = false;
    });
  });
};

const getAplusMetaItem = (
  {
    spmC,
    spmD,
    data,
    cssSelectorC,
    cssSelectorIndex,
    args,
    trackInfo,
    ...others
  },
  type
) => ({
  ...others,
  // 直接使用logkey作为aplus自动埋点的选择器，规避cms配置间logkey重复问题
  cssSelector: '[data-ad][data-logkey-' + type + '="' + others.logkey + '"]',
});

const getDynamicNode = (element, configItem) => {
  // @ts-ignore
  if (window.DynamicDivTree) {
    if (element.__dynamic_div_unique_id)
      // @ts-ignore
      return window.DynamicDivTree.querySelectorAll(
        '$' + element.__dynamic_div_unique_id
      )[0];
    let domListIndex = -1;
    document
      .querySelectorAll(configItem.cssSelector)
      .forEach((e, i) => e === element && (domListIndex = i));
    // @ts-ignore
    const dynamicDivList = window.DynamicDivTree.querySelectorAll(
      configItem.cssSelector
    );
    if (domListIndex !== -1) {
      return dynamicDivList[domListIndex];
    }
  }
};
const getFullDataSet = (element, configItem) => {
  // 将全局数据集以及对象的dataset叠放成当前令箭使用的完整数据集
  let dynamicDivData = getDynamicNode(element, configItem)?.dataPack || {};

  return {
    ...aplusDynamicData,
    ...dynamicDivData,
    ...element.dataset,
  };
};

// element->index的mapping
const indexMap = new Map();
const getElementIndex = (element, configItem, i = -1) => {
  let e_i = i;
  let indexTarget = element;
  let dynamicNode = getDynamicNode(element, configItem);
  if (dynamicNode) {
    const cssSelectorIndex =
      configItem.cssSelectorIndex || dynamicNode.trackInfo?.cssSelectorIndex;
    if (cssSelectorIndex) dynamicNode = dynamicNode.closest(cssSelectorIndex);
    return dynamicNode.displayIndex === undefined
      ? dynamicNode.index
      : dynamicNode.displayIndex;
  }
  if (configItem.cssSelectorIndex) {
    indexTarget = element.closest(configItem.cssSelectorIndex);
  }
  e_i = indexMap.get(indexTarget) ?? -1;
  if (e_i === -1)
    [...indexTarget.parentNode.children]?.forEach(
      (ee, ii) => ee === indexTarget && (e_i = ii)
    );
  indexMap.set(indexTarget, e_i);
  return e_i;
};

const mergeTrackInfo = (type, configItem, e_i, element) => {
  // CMS使用，尝试通过原配置内的trackInfo路径，
  // 从aplusDynamicData中取出CMS下发的trackInfo并返回。
  if (configItem.trackInfo) {
    let ti;
    if (configItem.trackInfo === 'dynamic-track-info') {
      ti = getDynamicNode(element, configItem)?.trackInfo;
    } else {
      const path = configItem.trackInfo.map(o =>
        o === ELEMENT_INDEX ? e_i : o
      );
      ti = get(getFullDataSet(element, configItem), path);
    }
    if (!ti) {
      if (window.goldlog?.aplusDebug)
        console.warn(
          'Aplus Dynamic: Merge TrackInfo Failed in',
          element,
          configItem
        );
      return;
    }
    const { args, spmc, spmd, exposeEventName, clickEventName, ...others } = ti;
    return {
      args: args,
      spmC: spmc,
      spmD: spmd,
      logkey: { exp: exposeEventName, clk: clickEventName }[type],
      ...others,
      ...configItem,
    };
  }
  return configItem;
};
// 防止重复进行异步配置，记录已配置过的logkey
let logkeyInitialized = {};
const aplusDomChangeListeners = [];
// 直接通过元素取到其对应config的主map
const elementConfigMap = { exp: new Map(), clk: new Map() };

/**
 * 初始化动态埋点配置
 * @param exp
 * @param clk
 * @param autoExpList 自动曝光配置列表
 * @param autoClkList 自动点击配置列表
 * @param isMerge true:合并到目前的配置中；false:覆盖目前的配置
 * @param isMergeMetaTag true:合并到目前Meta标签的配置；false:覆盖目前的配置
 */
export const setDynamicMetaInfo = ({
  exp = [],
  clk = [],
  autoExpList = exp,
  autoClkList = clk,
  isMerge = true,
  isMergeMetaTag = false,
  dynamicTrackInfo = false,
  instant = true,
  skipC = false,
}) => {
  window.goldlog_queue = window.goldlog_queue || [];
  if (!isMerge) {
    logkeyInitialized = {};
    indexMap.clear();
    elementConfigMap.clk.clear();
    elementConfigMap.exp.clear();
    aplusDomChangeListeners?.forEach(listener => {
      window.goldlog_queue?.push({
        action: 'goldlog.aplus_pubsub.unsubscribe',
        arguments: ['APLUS_AE_DOM_CHANGE', listener],
      });
    });
  }
  if (dynamicTrackInfo) {
    autoExpList.push(dynamicTrackInfoConfig);
    autoClkList.push(dynamicTrackInfoConfig);
  }
  // 使用Aplus内部发布订阅机制，订阅其限流后的DOM更新事件，在其中维护DOM内的data-spm配置
  // 本质上就是一个MutationObserver的回调过了一个200ms的throttle
  const domChangeListener = () => {
    const dirtyDOM = [];
    const configToPush = [];
    const fixElementSPM = type => currentConfigOriginal => {
      try {
        document
          .querySelectorAll(currentConfigOriginal.cssSelector)
          ?.forEach(e => {
            if (!e.getAttribute('data-ad')) {
              let currentConfig = currentConfigOriginal;
              let e_i = getElementIndex(e, currentConfigOriginal);
              currentConfig = mergeTrackInfo(type, currentConfig, e_i, e);
              if (!currentConfig?.logkey) return;
              if (window.goldlog?.aplusDebug)
                console.log(
                  'Aplus Dynamic: Fixing Element SPM:' + type,
                  currentConfig,
                  e
                );
              e.setAttribute('data-logkey-' + type, currentConfig.logkey);
              if (!dirtyDOM.includes(e)) {
                // 如果存在cssSelectorC则查找一下，如果没有或者找不到则丢到直接父亲上
                currentConfig.spmC &&
                  !skipC &&
                  (
                    (currentConfig.cssSelectorC &&
                      e.closest(currentConfig.cssSelectorC)) ||
                    e.parentNode
                  )?.setAttribute?.('data-spm', currentConfig.spmC);
                // spmD直接写进去，spmC也一并补偿进去以备其他用途（跳转spm，关系验证等）
                currentConfig.spmD &&
                  e.setAttribute(
                    'data-spm',
                    Array.isArray(currentConfig.spmD)
                      ? currentConfig.spmD
                          .map(oo => (oo === ELEMENT_INDEX ? e_i : oo))
                          .join('')
                      : currentConfig.spmD +
                          (currentConfig.spmD?.endsWith('_') ||
                          currentConfig.forceIndex
                            ? e_i
                            : '') // 约定如果spmD以_结尾，则拼接index。如果不需要这个特性，可以传入spmD手动覆盖。
                  );
                currentConfig.spmC &&
                  e.setAttribute('data-spm-c', currentConfig.spmC);
                // 先不写data-ad，全部处理完一轮后一并写入
                dirtyDOM.push(e);
              }
              elementConfigMap[type].set(e, currentConfigOriginal);
              if (!logkeyInitialized[currentConfig.logkey]) {
                logkeyInitialized[currentConfig.logkey] = true;
                configToPush.push([type, currentConfig]);
              }
            }
          });
      } catch (e) {
        console.warn(
          'Aplus Dynamic: Fix Element SPM Failed in',
          currentConfigOriginal
        );
      }
    };
    autoExpList?.forEach(fixElementSPM('exp'));
    autoClkList?.forEach(fixElementSPM('clk'));
    if (dirtyDOM.length) {
      dirtyDOM.forEach(e => {
        // 打标表示完成，此时会触发aplus自动曝光
        e.setAttribute('data-ad', '1');
      });
    }
    if (configToPush.length) {
      const opt = {
        autoExpList: [],
        autoClkList: [],
        isMerge: true,
        isMergeMetaTag,
      };
      configToPush.forEach(([type, cfg]) => {
        opt[{ exp: 'autoExpList', clk: 'autoClkList' }[type]].push(
          getAplusMetaItem(cfg, type)
        );
      });
      setAutoLogMetaInfo(opt);
    }
    if (dirtyDOM.length) {
      forceCheckAplusMO();
    }
  };
  // 使用tbm-log设置处理后的meta
  let autoClkListFiltered = autoClkList
    .filter(o => {
      if (o.logkey && !logkeyInitialized[o.logkey]) {
        logkeyInitialized[o.logkey] = true;
        return true;
      } else return false;
    })
    .map(o => getAplusMetaItem(o, 'clk'));
  let autoExpListFiltered = autoExpList
    .filter(o => {
      if (o.logkey && !logkeyInitialized[o.logkey]) {
        logkeyInitialized[o.logkey] = true;
        return true;
      } else return false;
    })
    .map(o => getAplusMetaItem(o, 'exp'));

  autoExpListFiltered?.push({
    // @ts-ignore
    logkey: '/a.fake.exp.log.to.enable.mutation.observer.' + Date.now(),
    cssSelector: '.a-fake-exp-log-' + Date.now(),
  });
  setAutoLogMetaInfo({
    autoExpList: autoExpListFiltered,
    autoClkList: autoClkListFiltered,
    isMerge,
    isMergeMetaTag,
  });
  // 如果过早挂监听，domChangeListener会setMetaInfo触发（init时会触发一次）
  // 此时会有一个不必要的aplus内部逻辑影响到clk埋点的预处理使得点击失败
  // 所以延后挂监听，但是在本函数末尾作出补偿，立即调用一次domChangeListener
  window.goldlog_queue?.push({
    action: 'goldlog.aplus_pubsub.subscribe',
    arguments: ['APLUS_AE_DOM_CHANGE', domChangeListener],
  });
  aplusDomChangeListeners.push(domChangeListener);
  // 自动点击曝光时补数据的回调
  const userFn = type => (element, selector) => {
    // 通过预处理的Mapping取到对应的配置对象
    let currentConfig = elementConfigMap[type].get(element);
    if (window.goldlog?.aplusDebug)
      console.log(
        'Aplus Dynamic: Merge ' + type + ' data:',
        element,
        selector,
        currentConfig
      );
    if (!currentConfig) return '';
    try {
      let e_i = getElementIndex(element, currentConfig);

      if (currentConfig.trackInfo) {
        currentConfig = mergeTrackInfo(type, currentConfig, e_i, element);
      }
      if (!currentConfig) return '';
      const ret = currentConfig.args ? { ...currentConfig.args } : {};
      const fullDataSet = getFullDataSet(element, currentConfig);
      const spm = getSpm({ dom: element });
      // 从数据集中取出配置项中声明的数据写入ret对象中
      currentConfig?.data?.forEach?.(k => {
        const key = Array.isArray(k)
          ? // 传入数组意为[上报key,实际存储位置]
            k[0]
          : // 否则认为两者相同
            k;
        let path = Array.isArray(k) ? k[1] || k[0] : k;
        if (Array.isArray(path) && path.indexOf(ELEMENT_INDEX) >= 0) {
          if (window.goldlog?.aplusDebug)
            console.log('ELEMENT_INDEX -> ', e_i, indexMap);
          path = path.map(o => (o === ELEMENT_INDEX ? e_i : o));
        }
        let value;
        if (path === ELEMENT_INDEX) {
          value = e_i;
        } else {
          value = get(fullDataSet, path) ?? get(fullDataSet, camelCase(path));
        }
        if (value === undefined) {
          if (window.goldlog?.aplusDebug)
            console.warn('Aplus Dynamic: Missing data for ' + key);
        } else ret[key] = value;
      });
      Object.keys(ret).forEach(k => {
        ret[k] = ret[k] === undefined ? '' : ret[k] + '';
      });

      ret['data-log'] = getAutoLogData({ ...ret, spmad: spm });
      return { userdata: ret, spm };
    } catch (e) {
      if (window.goldlog?.aplusDebug)
        console.warn(
          'Aplus Dynamic: Data Merge error in',
          element,
          selector,
          currentConfig,
          e
        );
      return '';
    }
  };

  // 挂回调
  window.goldlog_queue.push({
    action: 'goldlog.setMetaInfo',
    arguments: ['aplus-auto-exp-userfn', userFn('exp')],
  });
  window.goldlog_queue.push({
    action: 'goldlog.setMetaInfo',
    arguments: ['aplus-auto-clk-userfn', userFn('clk')],
  });

  instant && domChangeListener();
};
